import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpBaseService } from '../httpBase/httpBase.service';
import { ENDPOINTS_BUYER, ENDPOINTS_SUPPLIER } from '../../constants/ENDPOINTS';
import {
  CorrectionResponse,
  EvidenceMessagesResponse,
  InspectionCaseDetailResponse,
  InspectionCaseListState,
  InspectionCasesListResponse,
  PostCorrectionEntryRequest,
  PostCorrectionReplyResponse,
  PostEvidenceRequest,
  PostEvidenceResponse,
  PostProposalResponse,
  PostReactionRequest,
  PostReactionResponse,
} from '../../models/inspection';

export type InspectionAudience = 'buyer' | 'supplier';

/** Either the incremental read or a page of history — never both at once. */
export type GetMessagesOptions =
  | { afterId: number }
  | { page?: number; perPage?: number };

/** The list endpoint's only filter, plus its own pagination params. */
export interface GetCasesOptions {
  state?: InspectionCaseListState;
  page?: number;
  itemsPerPage?: number;
}

/**
 * Buyer and supplier Inspection Case routes are exact mirrors (contract
 * `api-contracts/inspection-module-api-contract-v2.md` §0). One service, routed by
 * `audience`, instead of two near-duplicate services.
 */
@Injectable({
  providedIn: 'root',
})
export class InspectionService extends HttpBaseService {
  /**
   * Every case the caller is a party to, newest first — the server enforces
   * this ordering, there is no client-controlled sort. Per-party only: a
   * buyer never sees a supplier's cases and vice versa.
   */
  getCases(
    audience: InspectionAudience,
    options: GetCasesOptions = {}
  ): Observable<InspectionCasesListResponse> {
    const base = this.endpoints(audience).GET_CASES;
    const params = new URLSearchParams();

    if (options.state) params.set('state', options.state);
    if (options.page) params.set('page', options.page.toString());
    if (options.itemsPerPage)
      params.set('items_per_page', options.itemsPerPage.toString());

    const query = params.toString();
    return this.get(query ? `${base}?${query}` : base);
  }

  getCase(
    audience: InspectionAudience,
    caseId: number
  ): Observable<InspectionCaseDetailResponse> {
    const url = this.endpoints(audience).GET_CASE.replace(
      '{case}',
      caseId.toString()
    );
    return this.get(url);
  }

  /**
   * `after_id` wins when present — `page`/`perPage` are dropped, matching the
   * contract's stated precedence.
   */
  getMessages(
    audience: InspectionAudience,
    caseId: number,
    options: GetMessagesOptions
  ): Observable<EvidenceMessagesResponse> {
    const base = this.endpoints(audience).GET_MESSAGES.replace(
      '{case}',
      caseId.toString()
    );

    if ('afterId' in options) {
      return this.get(`${base}?after_id=${options.afterId}`);
    }

    const params = new URLSearchParams();
    if (options.page) params.set('page', options.page.toString());
    if (options.perPage) params.set('per_page', options.perPage.toString());
    const query = params.toString();

    return this.get(query ? `${base}?${query}` : base);
  }

  postMessage(
    audience: InspectionAudience,
    caseId: number,
    payload: PostEvidenceRequest
  ): Observable<PostEvidenceResponse> {
    const url = this.endpoints(audience).POST_MESSAGE.replace(
      '{case}',
      caseId.toString()
    );
    return this.post(url, payload, undefined, true);
  }

  /**
   * The correction discussion — proposals with their own reactions and
   * replies nested inside, newest first.
   *
   * Paged only. The contract also offers the Evidence Log's `after_id`
   * incremental mode, and it is deliberately not implemented: a reaction lands
   * on a proposal that is *already* loaded, so a tail that only ever brings
   * entries with a greater id would never surface it.
   *
   * **404 is not an error here** — it is how the server says the Inspector has
   * not opened the discussion yet. Callers treat it as "no thread".
   */
  getCorrection(
    audience: InspectionAudience,
    caseId: number,
    options: { page?: number; perPage?: number } = {}
  ): Observable<CorrectionResponse> {
    const base = this.endpoints(audience).GET_CORRECTION.replace(
      '{case}',
      caseId.toString()
    );

    const params = new URLSearchParams();
    if (options.page) params.set('page', options.page.toString());
    if (options.perPage) params.set('per_page', options.perPage.toString());
    const query = params.toString();

    return this.get(query ? `${base}?${query}` : base);
  }

  /**
   * Only ever a *further* proposal: the first one on a case is the Inspector's
   * alone, posted Filament-side, and this 404s until that has happened.
   */
  postProposal(
    audience: InspectionAudience,
    caseId: number,
    payload: PostCorrectionEntryRequest
  ): Observable<PostProposalResponse> {
    const url = this.endpoints(audience).POST_CORRECTION_PROPOSAL.replace(
      '{case}',
      caseId.toString()
    );
    return this.post(url, payload, undefined, true);
  }

  /**
   * `in_reply_to_id` targets a proposal, which is purely what nests this reply
   * under it on the next read — it stops no clock and requires no reaction.
   */
  postCorrectionReply(
    audience: InspectionAudience,
    caseId: number,
    payload: PostCorrectionEntryRequest
  ): Observable<PostCorrectionReplyResponse> {
    const url = this.endpoints(audience).POST_CORRECTION_REPLY.replace(
      '{case}',
      caseId.toString()
    );
    return this.post(url, payload, undefined, true);
  }

  /**
   * Accept or reject one proposal. Recorded and immediately visible to every
   * party — it never changes the case's state and is never counted as a vote.
   */
  postReaction(
    audience: InspectionAudience,
    caseId: number,
    entryId: number,
    payload: PostReactionRequest
  ): Observable<PostReactionResponse> {
    const url = this.endpoints(audience)
      .POST_CORRECTION_REACTION.replace('{case}', caseId.toString())
      .replace('{entry}', entryId.toString());
    return this.post(url, payload, undefined, true);
  }

  private endpoints(audience: InspectionAudience) {
    return audience === 'buyer'
      ? ENDPOINTS_BUYER.INSPECTION_CASES
      : ENDPOINTS_SUPPLIER.INSPECTION_CASES;
  }
}
